import { differenceInDays, format } from "date-fns";
import { extractCategoriesData } from "@/lib/Helpers/categoryMappingUtils";
import {
  getRoomAllocationsFromRooms,
  getTravellersFromRooms,
} from "@/lib/Helpers/urlHelpers";
import { useLocationStore } from "@/lib/stores/locationStore";

export const getSearchTrackingData = (
  searchParams: URLSearchParams,
  isHotelOnly: boolean,
  searchId?: string | null,
  destinationAirportCode?: string
) => {
  // Extract travellers from rooms format (middleware ensures this parameter exists)
  const rooms = searchParams.get("rooms") || "";
  const travellers = getTravellersFromRooms(rooms);
  const roomAllocations = getRoomAllocationsFromRooms(rooms);
  const stars = searchParams.get("stars");
  const mealType = searchParams.get("mealType");
  const roomType = searchParams.get("roomType");
  const location = useLocationStore.getState().location;
  const flexibleOffers = searchParams.get("flexibleOffers");
  const bookOnOption = searchParams.get("bookOnOption");
  const specialDeals = searchParams.get("specialDeals");
  const rentalCar = searchParams.get("rentalCar");

  const adults = travellers.filter((age: string) => Number(age) >= 25).length;
  const infants = travellers.filter((age: string) => Number(age) < 2).length;
  const children = travellers.filter(
    (age: string) => Number(age) >= 2 && Number(age) < 25
  ).length;

  const from = searchParams.get("from");
  const to = searchParams.get("to");
  const duration = searchParams.get("duration");

  const openSearchDuration =
    from && to
      ? differenceInDays(
          new Date(to + "T00:00:00"),
          new Date(from + "T00:00:00")
        )
      : 0;

  const isFixedDateFilterType =
    duration === "0" || duration === openSearchDuration.toString();

  const destinationType = searchParams.get("cview")
    ? "hotel"
    : searchParams.get("city")
    ? "city"
    : searchParams.get("region")
    ? "region"
    : searchParams.get("country")
    ? "country"
    : "country";

  const categories = searchParams.get("categories");
  const categoriesData = extractCategoriesData(categories);
  const recommendation = searchParams.get("minRecommendation");
  const countryName = location.countryName;
  const regionName = location.regionName;
  const cityName = location.cityName;

  const response: any = {
    destinationType,
    dateFilterType: isFixedDateFilterType ? "fixed" : "open",
    openSearchDuration,
    roomWithSeaView: searchParams.get("oceanView") === "true" ? "yes" : "no",
    providerName: searchParams.get("tourOperator") || "ALL",
    priceSlider: Number(searchParams.get("price")) || 6000,
    departureTime:
      searchParams.get("flightDepartureTimeRange") ?? "00.00-24.00",
    returnTime: searchParams.get("flightArrivalTimeRange") ?? "00.00-24.00",
    adults,
    children,
    infants,
    numberOfGuests: adults + children + infants,
    departureDate: from ? format(new Date(from), "yyyyMMdd") : "",
    returnDate: to ? format(new Date(to), "yyyyMMdd") : "",
    duration: openSearchDuration,
    searchId: searchId ?? "",
    travellerRoomAllocations: Object.fromEntries(
      roomAllocations.map((guests, index) => [index, guests])
    ),
    lengthOfStay: duration,
    // TODO add destinationCode specific to /buchung and /bestaetigung
    destinationCode:
      destinationAirportCode ||
      searchParams.get("city") ||
      searchParams.get("region") ||
      searchParams.get("country") ||
      "",
    flightAndTransfer:
      searchParams.get("transfer") === "1" ||
      searchParams.get("railAndFly") === "1" ||
      searchParams.get("directFlight") === "1" ||
      searchParams.get("rentalCar") === "1"
        ? "yes"
        : "no",

    recommendation: recommendation,
    holidayType: categoriesData.holidayTypes.join(","),
    hotelServices: categoriesData.hotelCriteria.join(","),
    destinationCountryName: countryName ? countryName : "beliebig",
    destinationRegionName: regionName,
    destinationCityName: cityName,
    destinationName: cityName,
    hotelClass: Number(stars),
    mealPlan: mealType,
    roomType,
    flexibleOffers,
    bookOnOption,
    specialDeals,
    rentalCar,
  };

  if (!isHotelOnly) {
    // TODO inform platformData team that originName is no longer available as we fetch airports in the CSW not in the app
    const airports = searchParams.get("departureAirport")?.split(",") ?? [];
    response.originCode = (() => {
      if (airports.length === 0) return "beliebig";
      if (airports.length > 1) return `multi_${airports.length}`;
      return airports[0] || "beliebig";
    })();
  }

  return response;
};
